import React, {useContext, useState} from 'react';
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import {useFormContext} from "react-hook-form";
import useFieldArrayClean from "../../../../../hooks/use-field-array-clean.js";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import FormField from "../../../../../components/react-hook-form/form-field.jsx";
import {useQuery} from "@tanstack/react-query";
import natureService from "../../../../../services/web/nature-service.js";
import {FaPlus, FaTrash} from "react-icons/fa";
import InputDateTime from "../../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import placeService from "../../../../../services/web/place-service.js";
import optionsUtils from "../../../../../utils/options-utils.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../../utils/location-utils.js";
import {MaskDefault} from "../../../../../utils/mask.js";
import {BoFormContext} from "../context/bo-form-context.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import ModalReverseGeocoding from "../../../../../components/modal/modal-reverse-geocoding.jsx";

const BoFormOccurrenceData = () => {
    const {setIsLoading} = useContext(BoFormContext);
    const { control, setValue, watch } = useFormContext();

    const [open, setOpen] = useState(false);

    const { fields, append, remove } = useFieldArrayClean({
        control,
        name: "occurrenceData.reason"
    });

    const {data: reason} = useQuery({
        queryFn: () => natureService.getByBoGcm(),
        queryKey: ['nature-dispatch'],
    });

    const {data: place = []} = useQuery({
        queryFn: () => placeService.getPlacesByBo(),
        queryKey: ['bo-place'],
    });

    const uncertain = watch("occurrenceData.dtFactUncertain");

    const handlePlace = (selected) => {
        setValue("occurrenceData.address.place", selected?.label ?? null);
        setValue("occurrenceData.address.zipCode", selected?.zipcode ?? selected?.zipCode ?? null);
        setValue("occurrenceData.address.address", selected?.street ?? null);
        setValue("occurrenceData.address.number", selected?.number ?? null);
        setValue("occurrenceData.address.neighborhood", selected?.neighborhood ?? null);
        setValue("occurrenceData.address.city", selected?.district ?? selected?.city ?? null);
        setValue("occurrenceData.address.complement", selected?.complement ?? null);
        setValue("occurrenceData.address.latitude", selected?.latitude ?? null);
        setValue("occurrenceData.address.longitude", selected?.longitude ?? null);
    }

    const handleAddressChange = (field, value) => {
        field.onChange(value);
        setValue("occurrenceData.address.place", null);
    }

    const reverseGeocoding = (address) => {
        if (address?.city && address.district) {
            setValue("occurrenceData.address.city", `${address?.city} - ${address?.district}`);
        }

        setValue("occurrenceData.address.place", null);
        setValue("occurrenceData.address.crossroads", null);
        setValue("occurrenceData.address.referencePoint", null);
        setValue("occurrenceData.address.address", address?.street ?? address?.address ?? null);
        setValue("occurrenceData.address.number", address?.number ?? null);
        setValue("occurrenceData.address.neighborhood", address?.neighborhood ?? null);
        setValue("occurrenceData.address.latitude", address?.latitude ?? null);
        setValue("occurrenceData.address.longitude", address?.longitude ?? null);
    }

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Naturezas</Card.Title>
                </Card.Header>
                <Card.Body className={"space-y-4"}>
                    {
                        fields.map((item, index) => (
                            <SentryRow className={"p-4 border border-[#e5e7eb] rounded-md relative"} key={"reason-" + item.itemId}>
                                <FormField
                                    name={`occurrenceData.reason.${index}`}
                                    customRender={field =>  (
                                        <SelectComponent
                                            instanceId={"reason-" + index}
                                            value={field.value?.nameView || field.value?.name}
                                            options={reason ?? []}
                                            label={"Natureza *"}
                                            classNameWrapper={"col-span-7"}
                                            onChange={(selected) => {
                                                setValue(`occurrenceData.reason.${index}.group`, selected?.group_reason ?? null);

                                                if (!selected) {
                                                    field.onChange({
                                                        itemId: item.itemId,
                                                        name: null,
                                                        nameView: null,
                                                        group: null
                                                    });
                                                    return;
                                                }

                                                field.onChange({
                                                    itemId: item.itemId,
                                                    name: selected.label,
                                                    nameView: selected.label,
                                                    group: selected.group_reason
                                                })
                                            }}
                                        />
                                    )}
                                />

                                <div className={"flex justify-between gap-4 col-span-5"}>
                                    <FormField
                                        as={InputForm}
                                        name={`occurrenceData.reason.${index}.group`}
                                        label={"Grupo"}
                                        classNameWrapper={"w-full"}
                                        disabled
                                    />

                                    <Button target={"danger"} onClick={() => remove(index)}>
                                        <FaTrash />
                                    </Button>
                                </div>
                            </SentryRow>
                        ))
                    }

                    <div
                        className="relative w-fit border border-transparent rounded-lg transition-[border-color,border-width] duration-150 ease-out"
                        data-required-action="section"
                        data-required-action-field="Natureza"
                    >
                        <Button onClick={() => append({ itemId: fields?.at(-1) ? (fields?.at(-1).itemId + 1) : 1, name: null, nameView: null, group: null })}>
                            <FaPlus /> Adicionar natureza
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Header>
                    <Card.Title>Dados de ocorrência</Card.Title>
                </Card.Header>
                <Card.Body>
                    <SentryRow>
                        <FormField
                            name={"occurrenceData.dtFact"}
                            customRender={(field) => (
                                <InputDateTime
                                    id={"dtFact"}
                                    selected={projectUtils.toDate(field?.value)}
                                    label={"D/H do fato *"}
                                    onChange={field.onChange}
                                    classNameWrapper={"col-span-2 " + (uncertain ? "hidden" : "")}
                                    dateFormat={"LL"}
                                />
                            )}
                        />
                        <FormField
                            name={"occurrenceData.dtFact"}
                            customRender={(field) => (
                                <InputDateTime
                                    id={"dtFact2"}
                                    selected={projectUtils.toDate(field?.value, "dd/MM/yyyy")}
                                    label={"D/H do fato *"}
                                    onChange={field.onChange}
                                    classNameWrapper={"col-span-2 " + (!uncertain ? "hidden" : "")}
                                    dateFormat={"L"}
                                />
                            )}
                        />
                        <FormField
                            name={"occurrenceData.dtFactUncertain"}
                            customRender={(field) => (
                                <InputCheckbox
                                    id={"dtFactUncertain"}
                                    checked={field.value}
                                    label={"Sem hora exata"}
                                    onChange={field.onChange}
                                    className={"col-span-3"}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.dtPlace"}
                            customRender={(field) => (
                                <InputDateTime
                                    id={"dtPlace"}
                                    selected={projectUtils.toDate(field?.value)}
                                    label={"D/H local"}
                                    onChange={field.onChange}
                                    classNameWrapper={"col-span-2"}
                                    dateFormat={"LL"}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.dtEnd"}
                            customRender={(field) => (
                                <InputDateTime
                                    id={"dtEnd"}
                                    selected={projectUtils.toDate(field?.value)}
                                    label={"D/H final *"}
                                    onChange={field.onChange}
                                    classNameWrapper={"col-span-2"}
                                    dateFormat={"LL"}
                                />
                            )}
                        />

                        <FormField
                            name={`occurrenceData.flagrant`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"flagrant"}
                                    value={field.value?.toString()}
                                    options={[{label: "Sim", value: "true"}, {label: "Não", value: "false"}]}
                                    label={"Flagrante *"}
                                    classNameWrapper={"col-span-3"}
                                    onChange={(selected) => field.onChange(selected?.value ? selected.value === "true" : null)}
                                />
                            )}
                        />

                        <FormField
                            name={`occurrenceData.address.place`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"place"}
                                    value={field.value}
                                    options={place ?? []}
                                    label={"Local pré-cadastrado"}
                                    classNameWrapper={"col-span-3"}
                                    onChange={handlePlace}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.address.zipCode"}
                            customRender={field => (
                                <InputForm
                                    id={"occurrenceData.address.zipCode"}
                                    name={"occurrenceData.address.zipCode"}
                                    label={"CEP"}
                                    classNameWrapper={"col-span-2"}
                                    mask={MaskDefault.cep}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(value) => handleAddressChange(field, value)}
                                />
                            )}
                        />

                        <FormField
                            name={`occurrenceData.address.address`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"address"}
                                    classNameWrapper={"col-span-3"}
                                    loadOptions={getStreets}
                                    value={optionsUtils.getValue(field.value)}
                                    isAsync={true}
                                    label={"Logradouro"}
                                    onChange={(selected) => handleAddressChange(field, selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.address.number"}
                            customRender={field => (
                                <InputForm
                                    id={"occurrenceData.address.number"}
                                    name={"occurrenceData.address.number"}
                                    label={"N°"}
                                    classNameWrapper={"col-span-1"}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(value) => handleAddressChange(field, value)}
                                />
                            )}
                        />

                        <FormField
                            name={`occurrenceData.address.neighborhood`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"neighborhood"}
                                    classNameWrapper={"col-span-3"}
                                    loadOptions={getNeighborhoods}
                                    value={optionsUtils.getValue(field.value)}
                                    isAsync={true}
                                    label={"Bairro *"}
                                    onChange={(selected) => handleAddressChange(field, selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            )}
                        />

                        <FormField
                            name={`occurrenceData.address.crossroads`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"crossroads"}
                                    classNameWrapper={"col-span-6"}
                                    loadOptions={getStreets}
                                    value={optionsUtils.getValue(field.value)}
                                    isAsync={true}
                                    label={"Cruzamento"}
                                    onChange={(selected) => field.onChange(selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            )}
                        />

                        <FormField
                            as={InputForm}
                            name={"occurrenceData.address.referencePoint"}
                            label={"Ponto de referência"}
                            classNameWrapper={"col-span-3"}
                        />

                        <FormField
                            name={`occurrenceData.address.city`}
                            customRender={field => (
                                <SelectComponent
                                    instanceId={"city"}
                                    classNameWrapper={"col-span-3"}
                                    loadOptions={getCities}
                                    value={optionsUtils.getValue(field.value)}
                                    isAsync={true}
                                    label={"Município"}
                                    onChange={(selected) => handleAddressChange(field, selected?.value)}
                                    cacheOptions
                                    defaultOptions={[]}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.address.complement"}
                            customRender={field => (
                                <InputForm
                                    id={"occurrenceData.address.complement"}
                                    name={"occurrenceData.address.complement"}
                                    label={"Complemento"}
                                    classNameWrapper={"col-span-6"}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(value) => handleAddressChange(field, value)}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.address.latitude"}
                            customRender={field => (
                                <InputForm
                                    id={"occurrenceData.address.latitude"}
                                    name={"occurrenceData.address.latitude"}
                                    label={"Latitude *"}
                                    classNameWrapper={"col-span-2"}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(value) => handleAddressChange(field, value)}
                                />
                            )}
                        />

                        <FormField
                            name={"occurrenceData.address.longitude"}
                            customRender={field => (
                                <InputForm
                                    id={"occurrenceData.address.longitude"}
                                    name={"occurrenceData.address.longitude"}
                                    label={"Longitude *"}
                                    classNameWrapper={"col-span-2"}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={(value) => handleAddressChange(field, value)}
                                />
                            )}
                        />

                        <div className="col-span-2">
                            <Button type="button" target={"primary"} className={"w-full"} onClick={() => setOpen(true)}>
                                Mapa
                            </Button>
                        </div>
                    </SentryRow>
                </Card.Body>
            </Card>

            <ModalReverseGeocoding
                open={open}
                setOpen={setOpen}
                address={watch("occurrenceData.address")}
                setIsLoading={setIsLoading}
                onConfirm={reverseGeocoding}
            />
        </>
    );
};

export default BoFormOccurrenceData;